
<sw-page class="xps-product-finder-detail">
    <template #smart-bar-header>
        <h2>{{ placeholder(productFinder, 'name', $tc('xps-product-finder.detail.title')) }}</h2>
    </template>

    <template #smart-bar-actions>
        <sw-button-group>
            <mt-button
                variant="secondary"
                :disabled="isLoading"
                @click="onCancel">
                {{ $tc('xps-product-finder.detail.buttonCancel') }}
            </mt-button>

            <sw-button-process
                class="xps-product-finder-detail__save-action"
                :is-loading="isLoading"
                :process-success="processSuccess"
                variant="primary"
                @update:process-success="saveFinish"
                @click="onSave">
                {{ $tc('xps-product-finder.detail.buttonSave') }}
            </sw-button-process>
        </sw-button-group>
    </template>

    <template #content>
        <sw-card-view>
            <mt-card
                v-if="productFinder"
                :title="$tc('xps-product-finder.detail.cardTitleGeneral')"
                :is-loading="isLoading">

                <sw-container columns="repeat(auto-fit, minmax(250px, 1fr))" gap="0px 30px">
                    <mt-text-field
                        v-model="productFinder.name"
                        :label="$tc('xps-product-finder.detail.labelName')"
                        :placeholder="placeholder(productFinder, 'name', $tc('xps-product-finder.detail.placeholderName'))"
                        :error="productFinderNameError"
                        required>
                    </mt-text-field>

                    <mt-switch
                        v-model="productFinder.active"
                        :label="$tc('xps-product-finder.detail.labelActive')">
                    </mt-switch>
                </sw-container>

                <mt-textarea
                    v-model="productFinder.description"
                    :label="$tc('xps-product-finder.detail.labelDescription')"
                    :placeholder="placeholder(productFinder, 'description', $tc('xps-product-finder.detail.placeholderDescription'))">
                </mt-textarea>

                <mt-number-field
                    v-model="productFinder.position"
                    number-type="int"
                    :label="$tc('xps-product-finder.detail.labelPosition')">
                </mt-number-field>
            </mt-card>

            <mt-card
                v-if="productFinder"
                :title="$tc('xps-product-finder.detail.cardTitleSteps')"
                :is-loading="isLoading">

                <template #toolbar>
                    <mt-button
                        size="small"
                        variant="primary"
                        @click="addStep">
                        {{ $tc('xps-product-finder.detail.buttonAddStep') }}
                    </mt-button>
                </template>

                <!-- Steps Content -->
                <div v-if="productFinder.steps && productFinder.steps.length > 0" class="steps-container">
                    <mt-card
                        v-for="(step, stepIndex) in productFinder.steps"
                        :key="step.id"
                        class="xps-product-finder-step"
                        :title="`${stepIndex + 1}. ${step.name || 'Unnamed Step'}`">

                        <template #toolbar>
                            <mt-button
                                size="small"
                                variant="critical"
                                @click="removeStep(step.id)">
                                {{ $tc('xps-product-finder.detail.buttonRemoveStep') }}
                            </mt-button>
                        </template>

                        <sw-container columns="repeat(auto-fit, minmax(250px, 1fr))" gap="0px 30px">
                            <mt-text-field
                                v-model="step.name"
                                :label="$tc('xps-product-finder.detail.labelStepName')"
                                required>
                            </mt-text-field>

                        </sw-container>

                        <mt-text-field
                            v-model="step.question"
                            :label="$tc('xps-product-finder.detail.labelStepQuestion')">
                        </mt-text-field>

                        <mt-textarea
                            v-model="step.description"
                            :label="$tc('xps-product-finder.detail.labelStepDescription')">
                        </mt-textarea>

                        <mt-number-field
                            v-model="step.position"
                            number-type="int"
                            :label="$tc('xps-product-finder.detail.labelStepPosition')">
                        </mt-number-field>

                        <mt-card
                            :title="$tc('xps-product-finder.detail.cardTitleOptions')"
                            class="xps-product-finder-options">

                            <template #toolbar>
                                <mt-button
                                    size="small"
                                    variant="primary"
                                    @click="addOption(step)">
                                    {{ $tc('xps-product-finder.detail.buttonAddOption') }}
                                </mt-button>
                            </template>

                            <div v-if="step.options && step.options.length > 0" class="options-grid">
                                <mt-card
                                    v-for="(option, optionIndex) in step.options"
                                    :key="option.id"
                                    class="option-card"
                                    :title="`${optionIndex + 1}. ${option.name || 'Unnamed Option'}`">

                                    <template #toolbar>
                                        <mt-button
                                            size="small"
                                            variant="critical"
                                            @click="removeOption(step, option.id)">
                                            {{ $tc('xps-product-finder.detail.buttonRemoveOption') }}
                                        </mt-button>
                                    </template>

                                    <mt-text-field
                                        v-model="option.name"
                                        :label="$tc('xps-product-finder.detail.labelOptionName')"
                                        required>
                                    </mt-text-field>

                                    <mt-textarea
                                        v-model="option.description"
                                        :label="$tc('xps-product-finder.detail.labelOptionDescription')">
                                    </mt-textarea>

                                    <sw-media-field
                                        v-model:value="option.imageId"
                                        :label="$tc('xps-product-finder.detail.labelOptionImage')">
                                    </sw-media-field>

                                    <mt-text-field
                                        v-model="option.value"
                                        :label="$tc('xps-product-finder.detail.labelOptionValue')">
                                    </mt-text-field>

                                    <mt-number-field
                                        v-model="option.position"
                                        number-type="int"
                                        :label="$tc('xps-product-finder.detail.labelOptionPosition')">
                                    </mt-number-field>

                                    <!-- Product Selection -->
                                    <sw-entity-multi-id-select
                                        v-model:value="option.productIds"
                                        :label="$tc('xps-product-finder.detail.labelOptionProducts')"
                                        :repository="productRepository"
                                        :criteria="productCriteria">
                                    </sw-entity-multi-id-select>
                                </mt-card>
                            </div>

                            <!-- Options Empty State -->
                            <div v-else class="empty-state-container">
                             <p>{{ $tc('xps-product-finder.detail.emptyStateOptions') }}</p>
                            </div>
                        </mt-card>
                    </mt-card>
                </div>

                <!-- Steps Empty State -->
                <div v-else class="empty-state-container">
                <p>{{ $tc('xps-product-finder.detail.emptyStateSteps') }}</p>

                </div>
            </mt-card>
        </sw-card-view>
    </template>
</sw-page>
